import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, ButtonBase, useMediaQuery, useTheme } from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { loadStripe } from '@stripe/stripe-js';

import { checkoutAPIService } from '~/api/checkout';
import FolderIcon from '~/assets/icons/content-qr/folder.svg?react';
import CreateQrIcon from '~/assets/icons/sidebar/create-qr.svg?react';
import CreateEditFolderModal from '~/components/folders/CreateEditFolderModal';
import { ConfirmModal } from '~/components/ui/ConfirmModal/ConfirmModal';
import { Text } from '~/components/ui/text';
import { UpgradeContent } from '~/components/ui/UpgradeContent/UpgradeContent';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { PATH } from '~/constants/route-path';
import useQrCodeFolders from '~/hooks/useQrCodeFolders';
import { useAppDispatch } from '~/hooks/useRedux';
import MobileCreateActionsDrawer from '~/pages/MyQR/components/MobileCreateActionsDrawer';
import MyFolders from '~/pages/MyQR/components/MyFolders';
import { getLoggedInUser } from '~/store/reducers/account/selector';
import { getAccountThunk } from '~/store/reducers/account/slice';
import { accountSlice } from '~/store/reducers/account/slice';
import { createQrCodeStylesThunk } from '~/store/reducers/qr-code';
import { COLORS } from '~/styles/colors';
import { logger } from '~/utils/logger';

import QRTable from './components/QrTable/QRTable';
import * as S from './styles';

export default function MyQR() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const params = useParams<{ folderId: string | undefined; folderName: string | undefined }>();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const [openModal, setOpenModal] = useState(false);
  const [isOpenCreateFolderModal, setIsOpenCreateFolderModal] = useState(false);

  const currency = localStorage.getItem('currency') || 'usd';

  const { isSubscriptionValid, loginPriceValue } = useSelector(getLoggedInUser);

  const styles = useStyles();

  const { data: folders } = useQrCodeFolders();

  const handleItemDetailClick = (id: number, name: string) => {
    navigate(location.pathname + '/detail', {
      state: {
        qrId: id,
        name,
      },
    });
  };

  const redirectToCheckout = async () => {
    if (loginPriceValue) {
      const config = await checkoutAPIService.getConfig();
      const stripe = await loadStripe(config?.data?.publicKey);
      const response = await checkoutAPIService.createPriceSession(loginPriceValue, currency);

      dispatch(accountSlice.actions.setLoginPriceValue(null));

      const { error } = await stripe.redirectToCheckout({
        sessionId: response?.data?.sessionId,
      });

      if (error) {
        logger.error('Error redirecting to Stripe Checkout:', error);
      }
    }
  };

  const handleToCreate = () => {
    if (isSubscriptionValid) {
      navigate(PATH.QR.CREATE);
    } else {
      setOpenModal(true);
    }
  };

  const handleCreateFolder = () => {
    setIsOpenCreateFolderModal(true);
  };

  useEffect(() => {
    dispatch(createQrCodeStylesThunk(true));
    dispatch(getAccountThunk());
    void redirectToCheckout();
  }, []);

  return (
    <Box sx={S.myQRcontainer}>
      <Box sx={S.myQrTopCard}>
        <Text variant="h2" sx={{ color: COLORS.textMain }}>
          {params.folderName ?? t('labels:myQrTitle')}
        </Text>
        {isDesktop ? (
          <Box sx={styles.buttons}>
            <ButtonBase onClick={handleCreateFolder} sx={styles.createFolder} disableRipple>
              <FolderIcon width={24} height={24} />
              <Text variant="h3" sx={{ color: COLORS.primary }}>
                {t('builder:nameAndFolder.createFolder')}
              </Text>
            </ButtonBase>
            <ButtonBase onClick={handleToCreate} sx={styles.createQr} disableRipple>
              <CreateQrIcon width={24} height={24} />
              <Text variant="h3" sx={{ color: COLORS.white }}>
                {t('sidebar:newQr')}
              </Text>
            </ButtonBase>
          </Box>
        ) : (
          <MobileCreateActionsDrawer onCreateQr={handleToCreate} onCreateFolder={handleCreateFolder} />
        )}
        {isOpenCreateFolderModal && (
          <CreateEditFolderModal
            isOpen={isOpenCreateFolderModal}
            onClose={() => setIsOpenCreateFolderModal(false)}
            type="create"
          />
        )}
      </Box>
      {params.folderId ? (
        <StyledBackLink to={PATH.QR.MAIN}>
          <ChevronLeftIcon />
          <Text variant="buttonLinedMed">{t('buttons:back')}</Text>
        </StyledBackLink>
      ) : (
        <>{!!folders?.length && <MyFolders />}</>
      )}
      <QRTable onDetailClick={handleItemDetailClick} />
      <ConfirmModal onClose={() => setOpenModal(false)} open={openModal}>
        <UpgradeContent />
      </ConfirmModal>
    </Box>
  );
}

function useStyles() {
  return {
    buttons: css`
      display: flex;
      gap: 20px;
      align-items: center;
    `,
    createFolder: css`
      padding: 8px 24px;
      gap: 8px;
      border-radius: 8px;
      border: 1px solid ${COLORS.primary};
      height: fit-content;

      path,
      line {
        stroke: ${COLORS.primary};
      }
    `,
    createQr: css`
      padding: 8px 24px;
      gap: 8px;
      border-radius: 8px;
      background-color: ${COLORS.primary};
      height: fit-content;

      path,
      line {
        stroke: ${COLORS.white};
      }
    `,
  };
}

const StyledBackLink = styled(Link)`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 2px;
  color: ${COLORS.textMain};
  width: fit-content;
`;
